/**
 * 个人中心（轻量）
 * 账户信息 / 信用分 / 消息通知 / 收藏 / 评价管理 / 常用操作 / 团队管理（企业子账号 P0-8）
 * 联调对齐（M2-2 遗留 #4）：评价列表改调后端 GET /reviews/my
 * 联调对齐（P0-8 2026-08-21）：企业主账号可管理自己的子账号
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/stores/auth';
import { listMyReviews, listOrders } from '@/api/trade';
import { getPaymentPasswordStatus, setPaymentPassword } from '@/api/payment';
import { validatePayPassword } from '@/utils/paymentPassword';
import {
  createSubAccount,
  disableSubAccount,
  enableSubAccount,
  listSubAccounts,
  type CreateSubAccountParams,
  type SubAccount,
} from '@/api/subAccount';
import type { NotificationItem, ReviewItem, TradeOrder } from '@/types';
import { USER_TYPE, isOrderSettled, type StatusMeta } from '@/utils/constants';
import { formatDateTime, maskPhone, timeAgo } from '@/utils/format';
import Empty from '@/components/common/Empty';
import PageHero from '@/components/common/PageHero';
import '@/styles/pages/user.css';
import '@/styles/components/payment-password.css';

const MOCK_NOTIFICATIONS: NotificationItem[] = [
  { id: 1, type: 'order', title: '订单待付款', content: '您的订单已生成，请在 15 分钟内完成支付，超时自动取消。', read: false, created_at: new Date(Date.now() - 600000).toISOString() },
  { id: 2, type: 'auction', title: '出价被超越', content: '您在竞价中的出价已被超越，请及时跟进。', read: false, created_at: new Date(Date.now() - 1800000).toISOString() },
  { id: 3, type: 'system', title: '交割凭证提醒', content: '请上传不少于 3 张交割凭证完成交割确认。', read: true, created_at: new Date(Date.now() - 86400000).toISOString() },
];

const NOTIFY_TYPE_LABEL: Record<string, string> = {
  order: '订单',
  auction: '竞价',
  broker: '经纪',
  system: '系统',
};

/**
 * 订单概览分桶（P0-5 8 态 + 已结算派生，对齐后端 model/trade.go 代码口径）：
 * 0-待付款 1-已付款 2-已发货 3-已完成 4-已取消 5-已退款 6-已过期 7-售后中；
 * 「已结算」= Completed(3)+settled_at；各桶互斥分区。
 */
const ORDER_BUCKETS: { key: string; label: string; tone: StatusMeta['tone']; match: (o: TradeOrder) => boolean }[] = [
  { key: 'pending', label: '待付款', tone: 'warning', match: (o) => o.order_status === 0 },
  { key: 'paid', label: '已付款', tone: 'info', match: (o) => o.order_status === 1 },
  { key: 'shipped', label: '已发货', tone: 'info', match: (o) => o.order_status === 2 },
  { key: 'completed', label: '已完成', tone: 'success', match: (o) => o.order_status === 3 && !isOrderSettled(o) },
  { key: 'settled', label: '已结算', tone: 'gold', match: (o) => isOrderSettled(o) },
  { key: 'aftersales', label: '售后中', tone: 'gold', match: (o) => o.order_status === 7 },
  { key: 'cancelled', label: '已取消', tone: 'danger', match: (o) => o.order_status === 4 },
  { key: 'refunded', label: '已退款', tone: 'danger', match: (o) => o.order_status === 5 },
  { key: 'expired', label: '已过期', tone: 'danger', match: (o) => o.order_status === 6 },
];

/** 用户类型常量（与后端 model.UserTypeXxx 对齐） */
const USER_TYPE_ENTERPRISE = 3;

/** 子账号创建表单初始值 */
const EMPTY_SUB_FORM: CreateSubAccountParams = {
  username: '',
  password: '',
  phone: '',
  real_name: '',
  role_codes: [],
};

export default function UserCenter() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'overview';

  const [notifications, setNotifications] = useState<NotificationItem[]>(MOCK_NOTIFICATIONS);
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState('');
  const [collections] = useState<number[]>([]);

  // 订单概览（P0-5：最近订单按状态分桶，含已结算/售后中/已过期）
  const [recentOrders, setRecentOrders] = useState<TradeOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [ordersError, setOrdersError] = useState('');

  // 子账号状态（P0-8，仅企业主账号可见）
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([]);
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreateSubAccountParams>(EMPTY_SUB_FORM);
  const [formError, setFormError] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // 支付密码状态（资金 2FA · P0 资金安全）
  const [paypwdLoading, setPaypwdLoading] = useState(false);
  const [paypwdStatus, setPaypwdStatus] = useState<boolean | null>(null); // null=未知/加载失败
  const [paypwdStatusError, setPaypwdStatusError] = useState('');
  // 设置 / 修改表单
  const [ppPwd, setPpPwd] = useState('');
  const [ppPwd2, setPpPwd2] = useState('');
  const [ppSubmitting, setPpSubmitting] = useState(false);
  const [ppMsg, setPpMsg] = useState('');
  const [ppError, setPpError] = useState('');

  const isEnterprise = user?.user_type === USER_TYPE_ENTERPRISE;

  const loadReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const data = await listMyReviews();
      setReviews(data);
      setReviewsError('');
    } catch (e) {
      setReviewsError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  const loadSubAccounts = useCallback(async () => {
    setSubLoading(true);
    setSubError('');
    try {
      const data = await listSubAccounts();
      setSubAccounts(data);
    } catch (e) {
      setSubError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setSubLoading(false);
    }
  }, []);

  const loadPaypwdStatus = useCallback(async () => {
    setPaypwdLoading(true);
    setPaypwdStatusError('');
    try {
      const data = await getPaymentPasswordStatus();
      setPaypwdStatus(data.set);
    } catch (e) {
      setPaypwdStatus(null);
      setPaypwdStatusError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setPaypwdLoading(false);
    }
  }, []);

  /** 设置 / 修改支付密码：前端校验 6 位数字 + 两次一致，再调 PUT /users/me/payment-password */
  const handleSavePaypwd = async () => {
    setPpMsg('');
    setPpError('');
    const msg = validatePayPassword(ppPwd);
    if (msg) {
      setPpError(msg);
      return;
    }
    if (ppPwd !== ppPwd2) {
      setPpError('两次输入的支付密码不一致');
      return;
    }
    setPpSubmitting(true);
    try {
      await setPaymentPassword(ppPwd);
      setPpMsg('支付密码设置成功');
      setPpPwd('');
      setPpPwd2('');
      setPaypwdStatus(true);
    } catch (e) {
      setPpError(e instanceof Error ? e.message : '设置失败，请稍后重试');
    } finally {
      setPpSubmitting(false);
    }
  };

  /** 概览页订单分桶数据：合并买入/卖出两视角最近订单（各最多 100 条） */
  const loadRecentOrders = useCallback(async () => {
    if (!user) return;
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const [buy, sell] = await Promise.all([
        listOrders('buy', user.id),
        listOrders('sell', user.id),
      ]);
      const merged = new Map<number, TradeOrder>();
      [...buy, ...sell].forEach((o) => merged.set(o.id, o));
      setRecentOrders([...merged.values()]);
      setOrdersLoaded(true);
    } catch (e) {
      setOrdersError(e instanceof Error ? e.message : '订单加载失败');
    } finally {
      setOrdersLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (tab === 'review') {
      loadReviews();
    }
    if (tab === 'team' && isEnterprise) {
      loadSubAccounts();
    }
    if (tab === 'security') {
      loadPaypwdStatus();
    }
    if (tab === 'overview' && user && !ordersLoaded && !ordersLoading) {
      loadRecentOrders();
    }
  }, [tab, loadReviews, loadSubAccounts, loadRecentOrders, loadPaypwdStatus, isEnterprise, user, ordersLoaded, ordersLoading]);

  const switchTab = useCallback(
    (key: string) => {
      setSearchParams({ tab: key });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (searchParams.get('tab') === 'notify') {
      switchTab('notify');
    }
  }, [searchParams, switchTab]);

  const markRead = (id: number) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // 子账号表单本地校验（与后端 binding tag 一致）
  const validateSubForm = (): string | null => {
    if (form.username.length < 4 || form.username.length > 64) return '用户名长度需 4-64 字符';
    if (form.password.length < 8 || form.password.length > 64) return '密码长度需 8-64 字符';
    if (!/^\d{11}$/.test(form.phone)) return '手机号必须 11 位数字';
    if (form.real_name && form.real_name.length > 64) return '真实姓名最多 64 字符';
    if (form.role_codes && form.role_codes.length > 10) return '独立角色最多 10 个';
    return null;
  };

  const handleCreateSub = async () => {
    setFormError('');
    const err = validateSubForm();
    if (err) {
      setFormError(err);
      return;
    }
    setCreating(true);
    try {
      await createSubAccount(form);
      setCreateOpen(false);
      setForm(EMPTY_SUB_FORM);
      await loadSubAccounts();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : '创建失败');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleSubStatus = async (sub: SubAccount) => {
    setActionLoading(sub.id);
    try {
      if (sub.status === 1) {
        await disableSubAccount(sub.id);
      } else {
        await enableSubAccount(sub.id);
      }
      await loadSubAccounts();
    } catch (e) {
      setSubError(e instanceof Error ? e.message : '操作失败');
    } finally {
      setActionLoading(null);
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;
  const subAccountCount = useMemo(() => subAccounts.length, [subAccounts]);

  // 订单分桶计数（已结算/已完成互斥，见 ORDER_BUCKETS）
  const orderCounts = useMemo(() => {
    const counts = new Map<string, number>(ORDER_BUCKETS.map((b) => [b.key, 0]));
    recentOrders.forEach((o) => {
      const bucket = ORDER_BUCKETS.find((b) => b.match(o));
      if (bucket) counts.set(bucket.key, (counts.get(bucket.key) ?? 0) + 1);
    });
    return counts;
  }, [recentOrders]);

  return (
    <div className="user-page">
      <PageHero eyebrow="MY ACCOUNT" title="个人中心" desc="账户信息 · 信用分 · 消息 · 收藏 · 评价 · 团队管理" />
      <div className="container">
        <div className="user-layout">
          {/* 左：账户卡 */}
          <aside className="user-side">
            <div className="user-card user-profile">
              <span className="user-profile__avatar">{user?.real_name?.slice(0, 1) || user?.username?.slice(0, 1) || '牛'}</span>
              <strong>{user?.real_name || user?.username || '用户'}</strong>
              <span className="user-profile__type">{USER_TYPE[user?.user_type ?? 4] ?? '用户'}</span>
              {user?.enterprise_name ? <span className="user-profile__company">{user.enterprise_name}</span> : null}
              <span className="user-profile__phone">{maskPhone(user?.phone)}</span>
              <div className="user-profile__credit">
                <span>信用分</span>
                <b>92</b>
              </div>
            </div>

            <div className="user-card user-menu">
              <button className={`user-menu__item ${tab === 'overview' ? 'active' : ''}`} onClick={() => switchTab('overview')}>
                账户概览
              </button>
              <button className={`user-menu__item ${tab === 'notify' ? 'active' : ''}`} onClick={() => switchTab('notify')}>
                消息通知{unreadCount > 0 ? <em>{unreadCount}</em> : null}
              </button>
              <button className={`user-menu__item ${tab === 'favorite' ? 'active' : ''}`} onClick={() => switchTab('favorite')}>
                我的收藏
              </button>
              <button className={`user-menu__item ${tab === 'review' ? 'active' : ''}`} onClick={() => switchTab('review')}>
                我的评价
              </button>
              <button className={`user-menu__item ${tab === 'security' ? 'active' : ''}`} onClick={() => switchTab('security')}>
                支付密码
              </button>
              {isEnterprise ? (
                <button className={`user-menu__item ${tab === 'team' ? 'active' : ''}`} onClick={() => switchTab('team')}>
                  团队管理{subAccountCount > 0 ? <em>{subAccountCount}</em> : null}
                </button>
              ) : null}
            </div>
          </aside>

          {/* 右：内容区 */}
          <section className="user-content">
            {tab === 'overview' ? (
              <>
                <div className="user-card">
                  <div className="user-card__head user-card__head--flex">
                    我的订单概览
                    <span className="user-card__hint">最近订单（买卖视角各取最多 100 条）</span>
                    <Link className="user-card__more" to="/orders">全部订单 →</Link>
                  </div>
                  {ordersLoading ? (
                    <Empty icon="⏳" title="订单加载中…" />
                  ) : ordersError ? (
                    <div className="form-error" style={{ padding: 12 }}>
                      {ordersError}
                      <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={loadRecentOrders}>重试</button>
                    </div>
                  ) : recentOrders.length === 0 ? (
                    <Empty icon="📦" title="暂无订单" desc="去市场挑选心仪的牛只吧" action={<Link className="btn btn-primary btn-sm" to="/market">去逛逛</Link>} />
                  ) : (
                    <div className="user-order-stats">
                      {ORDER_BUCKETS.map((b) => (
                        <Link className="user-order-stat" to="/orders" key={b.key}>
                          <b className={`user-order-stat__num is-${b.tone}`}>{orderCounts.get(b.key) ?? 0}</b>
                          <span>{b.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <div className="user-card">
                  <div className="user-card__head">账户信息</div>
                  <div className="user-info-grid">
                    <div><span>用户名</span><b>{user?.username ?? '-'}</b></div>
                    <div><span>真实姓名</span><b>{user?.real_name ?? '-'}</b></div>
                    <div><span>用户类型</span><b>{USER_TYPE[user?.user_type ?? 4] ?? '-'}</b></div>
                    <div><span>手机号</span><b>{maskPhone(user?.phone)}</b></div>
                    <div><span>企业名称</span><b>{user?.enterprise_name ?? '-'}</b></div>
                    <div><span>账户状态</span><b>正常</b></div>
                  </div>

                  <div className="user-card__head">常用操作</div>
                  <div className="user-ops">
                    <Link to="/orders" className="user-op">📋 我的订单</Link>
                    <Link to="/my/listings" className="user-op">🏷️ 我的挂牌</Link>
                    <Link to="/my/wallet" className="user-op">💰 我的钱包</Link>
                    <Link to="/my/enterprise" className="user-op">🏢 企业入驻</Link>
                    <Link to="/listing/new" className="user-op">＋ 发布挂牌</Link>
                    <Link to="/broker/orders" className="user-op">🤝 经纪委托</Link>
                    <Link to="/auctions" className="user-op">🔨 竞价大厅</Link>
                    <Link to="/quotes" className="user-op">📊 行情看板</Link>
                    <Link to="/my?tab=security" className="user-op">🔐 支付密码</Link>
                  </div>

                  <button className="btn btn-danger" onClick={handleLogout}>退出登录</button>
                </div>
              </>
            ) : null}

            {tab === 'notify' ? (
              <div className="user-card">
                <div className="user-card__head">消息通知</div>
                {notifications.length === 0 ? (
                  <Empty icon="🔔" title="暂无消息" />
                ) : (
                  <div className="user-notify-list">
                    {notifications.map((n) => (
                      <div className={`user-notify ${n.read ? 'read' : ''}`} key={n.id} onClick={() => markRead(n.id)}>
                        <span className={`user-notify__type type-${n.type}`}>{NOTIFY_TYPE_LABEL[n.type]}</span>
                        <div className="user-notify__body">
                          <strong>{n.title}{!n.read ? <i /> : null}</strong>
                          <p>{n.content}</p>
                          <span className="user-notify__time">{timeAgo(n.created_at)} · {formatDateTime(n.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {tab === 'favorite' ? (
              <div className="user-card">
                <div className="user-card__head">我的收藏</div>
                {collections.length === 0 ? (
                  <Empty icon="⭐" title="暂无收藏" desc="在牛只市场收藏感兴趣的牛只" action={<Link className="btn btn-primary btn-sm" to="/market">去逛逛</Link>} />
                ) : null}
              </div>
            ) : null}

            {tab === 'review' ? (
              <div className="user-card">
                <div className="user-card__head">我的评价</div>
                {reviewsLoading ? (
                  <Empty icon="⏳" title="加载中…" />
                ) : reviewsError ? (
                  <div className="form-error" style={{ padding: 12 }}>{reviewsError}</div>
                ) : reviews.length === 0 ? (
                  <Empty icon="💬" title="暂无评价" desc="交易完成后 15 天内可评价，超期系统自动默认好评" />
                ) : (
                  <div className="user-review-list">
                    {reviews.map((r) => (
                      <div className="user-review" key={r.id}>
                        <span className="user-review__stars">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                        <p>{r.content || '（无内容）'}</p>
                        {r.reviewee_name ? <span className="user-review__to">评价对象：{r.reviewee_name}</span> : null}
                        {r.is_auto ? <span className="user-review__auto">系统默认好评</span> : null}
                        <span className="user-review__time">订单 #{r.order_id} · {formatDateTime(r.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {tab === 'security' ? (
              <div className="user-card">
                <div className="user-card__head user-card__head--flex">
                  支付密码（资金安全）
                  <span className="user-card__hint">订单支付等资金操作的双重校验（PRD §7.2 / P0 资金安全）</span>
                </div>

                {paypwdLoading ? (
                  <Empty icon="⏳" title="支付密码状态加载中…" />
                ) : paypwdStatusError ? (
                  <div className="form-error" style={{ padding: 12 }}>
                    {paypwdStatusError}
                    <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={loadPaypwdStatus}>
                      重试
                    </button>
                  </div>
                ) : (
                  <div className="ppwd-status-row">
                    <span>当前状态：</span>
                    {paypwdStatus === true ? (
                      <span className="ppwd-pill ppwd-pill--set">已设置</span>
                    ) : (
                      <span className="ppwd-pill ppwd-pill--unset">未设置</span>
                    )}
                    <span>
                      {paypwdStatus === true
                        ? '已设置支付密码，订单支付时需输入校验'
                        : '首次设置后即可在订单支付时完成安全校验'}
                    </span>
                  </div>
                )}

                {ppMsg ? <div className="ppwd-msg ppwd-msg--success">{ppMsg}</div> : null}
                {ppError ? <div className="ppwd-msg ppwd-msg--error">{ppError}</div> : null}

                <div className="ppwd-form">
                  <div className="form-group">
                    <label className="form-label">
                      <span className="req">*</span>支付密码
                    </label>
                    <input
                      className="form-control ppwd-input"
                      type="password"
                      inputMode="numeric"
                      autoComplete="new-password"
                      maxLength={6}
                      placeholder="6 位数字"
                      value={ppPwd}
                      disabled={ppSubmitting}
                      onChange={(e) => {
                        setPpPwd(e.target.value.replace(/\D/g, ''));
                        setPpMsg('');
                        setPpError('');
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <span className="req">*</span>确认支付密码
                    </label>
                    <input
                      className="form-control ppwd-input"
                      type="password"
                      inputMode="numeric"
                      autoComplete="new-password"
                      maxLength={6}
                      placeholder="再次输入 6 位数字"
                      value={ppPwd2}
                      disabled={ppSubmitting}
                      onChange={(e) => {
                        setPpPwd2(e.target.value.replace(/\D/g, ''));
                        setPpMsg('');
                        setPpError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !ppSubmitting) handleSavePaypwd();
                      }}
                    />
                  </div>
                  <div className="form-help">
                    支付密码为 6 位数字；连续输错 5 次将锁定 15 分钟，请妥善保管、勿与登录密码相同。
                  </div>
                  <div>
                    <button className="btn btn-primary" disabled={ppSubmitting} onClick={handleSavePaypwd}>
                      {ppSubmitting
                        ? '提交中…'
                        : paypwdStatus === true
                          ? '修改支付密码'
                          : '设置支付密码'}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {tab === 'team' && isEnterprise ? (
              <div className="user-card">
                <div className="user-card__head">
                  团队管理 · 子账号
                  <span className="user-card__hint">企业主账号专属 · 最多 50 个 · 员工子账号共享企业资质与权限</span>
                </div>

                {subError ? (
                  <div className="form-error" style={{ padding: 12 }}>{subError}</div>
                ) : null}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <button className="btn btn-primary" onClick={() => { setForm(EMPTY_SUB_FORM); setFormError(''); setCreateOpen(true); }}>
                    ＋ 新建子账号
                  </button>
                </div>

                {subLoading ? (
                  <Empty icon="⏳" title="加载中…" />
                ) : subAccounts.length === 0 ? (
                  <Empty icon="👥" title="暂无子账号" desc="为员工开通子账号，共享企业资质与权限" />
                ) : (
                  <div className="user-subaccount-list">
                    <div className="user-subaccount__head">
                      <span>用户名</span>
                      <span>姓名</span>
                      <span>手机号</span>
                      <span>状态</span>
                      <span>创建时间</span>
                      <span>操作</span>
                    </div>
                    {subAccounts.map((sub) => (
                      <div className="user-subaccount__row" key={sub.id}>
                        <span><b>{sub.username}</b></span>
                        <span>{sub.real_name || '-'}</span>
                        <span>{maskPhone(sub.phone)}</span>
                        <span>
                          {sub.status === 1 ? (
                            <em style={{ color: '#3f7a4d' }}>启用</em>
                          ) : (
                            <em style={{ color: '#a23b3b' }}>已禁用</em>
                          )}
                        </span>
                        <span>{formatDateTime(sub.created_at)}</span>
                        <span>
                          <button
                            className="btn btn-sm"
                            disabled={actionLoading === sub.id}
                            onClick={() => handleToggleSubStatus(sub)}
                          >
                            {sub.status === 1 ? '禁用' : '启用'}
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="user-card__hint" style={{ marginTop: 12 }}>
                  子账号登录后自动继承主账号（{user?.enterprise_name || '本企业'}）的全部权限。
                </div>

                {/* 新建子账号 Modal */}
                {createOpen ? (
                  <div className="modal-mask" onClick={() => !creating && setCreateOpen(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                      <div className="modal-card__head">
                        <strong>新建子账号</strong>
                        <button className="modal-card__close" onClick={() => setCreateOpen(false)} aria-label="关闭">×</button>
                      </div>
                      <div className="modal-card__body">
                        {formError ? <div className="form-error">{formError}</div> : null}
                        <label className="form-row">
                          <span>用户名 *</span>
                          <input
                            type="text"
                            placeholder="4-64 字符，全局唯一"
                            value={form.username}
                            onChange={(e) => setForm({ ...form, username: e.target.value })}
                          />
                        </label>
                        <label className="form-row">
                          <span>初始密码 *</span>
                          <input
                            type="text"
                            placeholder="8-64 字符，建议首登后立即修改"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                          />
                        </label>
                        <label className="form-row">
                          <span>手机号 *</span>
                          <input
                            type="text"
                            placeholder="11 位手机号，全局唯一"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          />
                        </label>
                        <label className="form-row">
                          <span>真实姓名</span>
                          <input
                            type="text"
                            placeholder="可选，最多 64 字符"
                            value={form.real_name ?? ''}
                            onChange={(e) => setForm({ ...form, real_name: e.target.value })}
                          />
                        </label>
                      </div>
                      <div className="modal-card__foot">
                        <button className="btn" onClick={() => setCreateOpen(false)} disabled={creating}>取消</button>
                        <button className="btn btn-primary" onClick={handleCreateSub} disabled={creating}>
                          {creating ? '创建中…' : '确认创建'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {tab === 'team' && !isEnterprise ? (
              <div className="user-card">
                <Empty icon="🔒" title="团队管理仅对企业主账号开放" desc="子账号是 P0-8 企业专属功能（PRD §5.1.4）" />
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}